import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import Layout from '../../../../components/Layout';
import { MainColumn } from '../../../../components/Layout/ThirdPaneLayout';
import PageBar from '../../../../components/PageBar/PageBar';
import Form from '../../../../components/Form/Form';
import FormRow from '../../../../components/Form/FormRow';
import FormColumn from '../../../../components/Form/FormColumn';
import { Input, Label } from '../../../../components/Form';
import Button from '../../../../components/Button/Button';
import useNotify from '../../../../hooks/useNotify';
import { Box, Flex, Text } from 'rebass';
import gmailSmtpRelayImg from '../../../../assets/gmail-smtp-relay-settings.png';
import {
  CREATE_SMTP_CREDENTIAL,
  DELETE_SMTP_CREDENTIAL,
  GET_SMTP_CREDENTIALS,
  TEST_SMTP_CONNECTION,
  UPDATE_SMTP_CREDENTIAL,
} from './EmailSettings.query';

const EmailSettings: React.FC = () => {
  const { showSuccess, showError } = useNotify();
  const history = useHistory();
  const { data, loading, error, refetch } = useQuery(GET_SMTP_CREDENTIALS);
  const [createSmtp] = useMutation(CREATE_SMTP_CREDENTIAL);
  const [updateSmtp] = useMutation(UPDATE_SMTP_CREDENTIAL);
  const [deleteSmtp] = useMutation(DELETE_SMTP_CREDENTIAL);
  const [testSmtp] = useMutation(TEST_SMTP_CONNECTION);

  const [formState, setFormState] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    fromAddress: '',
    fromName: '',
  });

  const smtpCredential = data?.me?.smtpCredential;

  useEffect(() => {
    if (smtpCredential) {
      setFormState({
        host: smtpCredential.host || '',
        port: smtpCredential.port || 587,
        username: smtpCredential.username || '',
        password: '', // Don't populate password for security
        fromAddress: smtpCredential.fromAddress || '',
        fromName: smtpCredential.fromName || '',
      });
    }
  }, [smtpCredential]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormState((prev) => {
      const val = type === 'number' ? parseInt(value) : value;

      return {
        ...prev,
        [name]: val,
      };
    });
  };

  const handleSave = async () => {
    if (!data?.me?.id) {
      showError('User profile not loaded');
      return;
    }
    try {
      const input = {
        ...formState,
        profileId: data.me.id,
      };
      if (smtpCredential) {
        await updateSmtp({ variables: { id: smtpCredential.id, input } });
        showSuccess('SMTP settings updated');
      } else {
        await createSmtp({ variables: { input } });
        showSuccess('SMTP settings created');
      }
      history.push('/company/profile');
    } catch (err) {
      showError('Failed to save SMTP settings', err);
    }
  };

  const handleTest = async () => {
    if (!data?.me?.id) {
      showError('User profile not loaded');
      return;
    }
    try {
      const input = {
        ...formState,
        profileId: data.me.id,
      };
      // If password is empty and we have existing credentials, we might need a way to test with existing password
      // but for now, the user must provide the password if they want to test a change or if it's new.
      const result = await testSmtp({ variables: { input } });
      if (result?.data?.testSmtpConnection) {
        showSuccess('SMTP Connection Successful!');
      } else {
        showError('SMTP Connection Failed');
      }
    } catch (err) {
      showError('SMTP Connection Error', err);
    }
  };

  const handleDelete = async () => {
    if (!smtpCredential) return;
    if (
      !window.confirm(
        'Are you sure you want to delete your custom SMTP settings? It will fallback to system default.'
      )
    )
      return;
    try {
      await deleteSmtp({ variables: { id: smtpCredential.id } });
      showSuccess('SMTP settings deleted');
      history.push('/company/profile');
    } catch (err) {
      showError('Failed to delete SMTP settings', err);
    }
  };

  if (loading) return <Layout>Loading...</Layout>;
  if (error) return <Layout>Error: {error.message}</Layout>;
  if (!data?.me) return <Layout>User profile not found</Layout>;

  return (
    <Layout>
      <MainColumn px="mainContent" pb="mainContent" height="inherit">
        <PageBar title="Email Settings" />
        <Flex sx={{ gap: 5, alignItems: 'flex-start' }}>
          {/* ── Left column: the form ── */}
          <Box sx={{ flex: '0 0 420px', minWidth: 0 }}>
            <Text mb={3} fontSize={1} color="gray">
              Configure your own SMTP server to send emails from your
              company&apos;s email address. If your provider requires IP
              whitelisting, add our Cloud IP: <strong>35.164.32.205</strong>.
            </Text>
            <Form onSubmit={(e) => e.preventDefault()}>
              <FormRow>
                <FormColumn>
                  <Label>SMTP Host</Label>
                  <Input
                    width="100%"
                    name="host"
                    value={formState.host}
                    onChange={handleChange}
                    placeholder="smtp.example.com"
                    autoComplete="off"
                    required
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Label>SMTP Port</Label>
                  <Input
                    width="100%"
                    name="port"
                    type="number"
                    value={formState.port}
                    onChange={handleChange}
                    autoComplete="off"
                    required
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Label>Username</Label>
                  <Input
                    width="100%"
                    name="username"
                    value={formState.username}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Label>Password</Label>
                  <Input
                    width="100%"
                    name="password"
                    type="password"
                    value={formState.password}
                    onChange={handleChange}
                    placeholder={
                      smtpCredential && formState.username ? '********' : ''
                    }
                    autoComplete="new-password"
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Label>From Address</Label>
                  <Input
                    width="100%"
                    name="fromAddress"
                    value={formState.fromAddress}
                    onChange={handleChange}
                    placeholder="noreply@yourcompany.com"
                    autoComplete="off"
                  />
                  <Text fontSize={1} mt={1} color="gray">
                    Optional. Defaults to your company profile email.
                  </Text>
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Label>From Name</Label>
                  <Input
                    width="100%"
                    name="fromName"
                    value={formState.fromName}
                    onChange={handleChange}
                    placeholder="Your Company Name"
                    autoComplete="off"
                  />
                  <Text fontSize={1} mt={1} color="gray">
                    Optional. Defaults to your company name.
                  </Text>
                </FormColumn>
              </FormRow>
              <Flex mt={4} sx={{ gap: 2 }}>
                <Button onClick={handleSave} variant="primary">
                  Save Settings
                </Button>
                <Button onClick={handleTest} variant="secondary">
                  Test Connection
                </Button>
                {smtpCredential && (
                  <Button onClick={handleDelete} variant="danger" ml="auto">
                    Delete
                  </Button>
                )}
              </Flex>
            </Form>
          </Box>

          {/* ── Right column: provider instructions ── */}
          <Box
            sx={{
              flex: '1 1 320px',
              minWidth: 260,
              borderLeft: '1px solid',
              borderColor: 'tableBorder',
              pl: 6,
              fontSize: 1,
              color: 'text',
            }}
          >
            {/* Gmail (personal accounts) */}
            <Box mb={4}>
              <Text fontWeight="bold" mb={2}>
                Gmail (Personal Accounts)
              </Text>
              <Text mb={2} color="gray">
                Basic password authentication is no longer supported by Google.
                You must use an App Password.
              </Text>
              <Box as="ol" sx={{ pl: 3, m: 0 }}>
                <Box as="li" mb={1}>
                  Go to your{' '}
                  <a
                    href="https://myaccount.google.com/security"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Account Security
                  </a>{' '}
                  page and enable <strong>2-Step Verification</strong>.
                </Box>
                <Box as="li" mb={1}>
                  Under &ldquo;2-Step Verification&rdquo;, scroll to{' '}
                  <strong>App passwords</strong> and create one for
                  &ldquo;Mail&rdquo;.
                </Box>
                <Box as="li" mb={1}>
                  Use <strong>smtp.gmail.com</strong>, port <strong>587</strong>
                  , your full Gmail address as the username, and the generated
                  App Password.
                </Box>
              </Box>
            </Box>

            {/* Google Workspace */}
            <Box mb={4}>
              <Text fontWeight="bold" mb={2}>
                Google Workspace (SMTP Relay)
              </Text>
              <Text mb={2} color="gray">
                The recommended approach for Google Workspace is to use the SMTP
                relay service with IP-based authentication — no username or
                password required.
              </Text>
              <Box as="ol" sx={{ pl: 3, m: 0 }}>
                <Box as="li" mb={1}>
                  In the{' '}
                  <a
                    href="https://admin.google.com/ac/apps/gmail/routing"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Admin console
                  </a>
                  , go to{' '}
                  <strong>Apps → Google Workspace → Gmail → Routing</strong> and
                  configure the <strong>SMTP relay service</strong>.
                </Box>
                <Box as="li" mb={1}>
                  Under <strong>Allowed senders</strong>, select{' '}
                  <em>Only addresses in my domains</em>.
                </Box>
                <Box as="li" mb={1}>
                  Enable{' '}
                  <strong>
                    Only accept mail from the specified IP addresses
                  </strong>{' '}
                  and add our Cloud IP: <strong>35.164.32.205</strong>.
                </Box>
                <Box as="li" mb={1}>
                  Leave <strong>Require SMTP Authentication</strong>{' '}
                  <em>unchecked</em> — and leave Username and Password blank in
                  the form on the left.
                </Box>
                <Box as="li" mb={1}>
                  Use <strong>smtp-relay.gmail.com</strong>, port{' '}
                  <strong>587</strong>.
                </Box>
              </Box>
              <Box
                as="img"
                src={gmailSmtpRelayImg}
                alt="Google Workspace SMTP relay recommended settings"
                sx={{
                  mt: 3,
                  maxWidth: 560,
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'tableBorder',
                }}
              />
            </Box>
          </Box>
        </Flex>
      </MainColumn>
    </Layout>
  );
};

export default EmailSettings;
